import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { ReportTable } from '@/components/ReportTable';
import { apiFetch } from '@/lib/api';
import { formatCents, todayISO } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface DailyCashRow {
  type: string;
  reference: string;
  party: string;
  direction: 'in' | 'out';
  amount: number;
}

interface DailyCashData {
  date: string;
  cash_in: { payments: number; pr_receipts: number; total: number };
  cash_out: { expenses: number; po_payments: number; refunds: number; total: number };
  net: number;
  rows: DailyCashRow[];
}

interface DailyCashResponse {
  data: DailyCashData;
}

// ─── Columns ───────────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'type', header: 'Type' },
  { key: 'reference', header: 'Reference' },
  { key: 'party', header: 'Party' },
  { key: 'direction', header: 'Direction' },
  { key: 'amount', header: 'Amount', money: true },
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function DailyCashReport() {
  const [date, setDate] = useState(() => todayISO());

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['report', 'daily-cash', date],
    queryFn: () => apiFetch<DailyCashResponse>(`/api/reports/daily-cash?date=${date}`),
  });

  const report = data?.data;
  const rows = (report?.rows ?? []) as unknown as Record<string, unknown>[];

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        <ChevronLeft className="h-3 w-3" />
        Reports
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Daily Cash Report</h1>
        <ExportButtons endpoint="/api/reports/daily-cash" params={{ date }} />
      </div>

      {/* Date filter */}
      <div className="flex items-center gap-2 mb-6">
        <Label htmlFor="date-input" className="text-sm text-muted-foreground">
          Date
        </Label>
        <Input
          id="date-input"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-36 h-8 text-sm"
        />
      </div>

      {/* Loading / error */}
      {isLoading && (
        <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>
      )}
      {isError && (
        <p className="text-sm text-destructive py-10 text-center">
          {error instanceof Error ? error.message : 'Failed to load report'}
        </p>
      )}

      {report && (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-1">
                <CardTitle className="text-sm font-medium text-muted-foreground">Cash In</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold tabular-nums">
                  {formatCents(report.cash_in.total)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-1">
                <CardTitle className="text-sm font-medium text-muted-foreground">Cash Out</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold tabular-nums">
                  {formatCents(report.cash_out.total)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-1">
                <CardTitle className="text-sm font-medium text-muted-foreground">Net</CardTitle>
              </CardHeader>
              <CardContent>
                <p
                  className={`text-2xl font-bold tabular-nums ${
                    report.net >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {formatCents(report.net)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Transactions table */}
          <ReportTable columns={COLUMNS} rows={rows} />
        </>
      )}
    </div>
  );
}
